import ColorBox from 'components/shared/colorBox';
import http from '../../../utility/http';
import {
  forbiddenCharacters,
  isEmpty,
  validateLength,
} from '../../../utility/validation';
import {
  asyncDebounce,
  getItemById,
  isArrayEmpty,
  trimString,
} from '../../../utility/helpers';
import { filterTags } from '../../../utility/tagUtils';
import { ReactComponent as TagsIcon } from '../../../assets/icons/tags-icon.svg';
import { FIELD_TYPES } from '../../../constants/fieldTypes';
import { API_UNIQUE_POSITION } from '../../../constants/apiRoutes';
import { CREATABLE_TAG_FIELD } from '../../../constants/tags';
import { PARAMS } from '../../../constants/pages';
import { KEY_JOB_TYPES } from '../../../constants/succession';

const { TEXT, SELECT, CHECKBOX } = FIELD_TYPES;

export const USERS_DEFAULT_PARAMS = {
  page: 1,
  ordering: 'first_name',
};

export const INITIAL_JOB_TITLE_DATA = {
  name: '',
  job_family: null,
  job_type: KEY_JOB_TYPES.GENERIC,
  is_key: false,
  criticality: null,
  description: '',
  tags: [],
};

export const prepareActiveOptions = (items = []) =>
  items.filter(item => item.is_active !== false);

export const jobFamilyLabel = (value, families = [], emptyLabel = '-') => {
  if (!value) return emptyLabel;

  return getItemById(families, value)?.name || emptyLabel;
};

export const prepareJobTitleTypeOptions = options => [
  { id: KEY_JOB_TYPES.SEAT, name: options.seat },
  { id: KEY_JOB_TYPES.GENERIC, name: options.generic },
];

export const criticalityLabel = (value, levels = [], emptyLabel = '-') => {
  if (!value) return emptyLabel;

  return getItemById(levels, value)?.label || emptyLabel;
};

export const prepareJobTitleFields = (
  onCreateTag,
  classes,
  { canManageJobTitle, canManageSuccessionSettings }
) => [
  {
    name: 'name',
    type: TEXT,
    fieldWrapperClass: classes.name,
    isDisabled: !canManageJobTitle,
    required: true,
    validators: [
      {
        validator: isEmpty,
        type: 'required',
      },
      {
        validator: forbiddenCharacters,
        type: 'forbiddenCharacters',
      },
      {
        validator: fieldValue => validateLength(fieldValue, 0, 255),
        type: 'nameMaxLength',
      },
      {
        type: 'availableName',
        validator: asyncDebounce(jobTitle => {
          const params = {
            name: trimString(jobTitle),
          };
          return http
            .get(API_UNIQUE_POSITION, { params })
            .then(({ data }) => !data.exists);
        }, 300),
        async: true,
      },
    ],
  },
  ...(canManageSuccessionSettings
    ? [
        {
          name: 'job_family',
          type: SELECT,
          fieldWrapperClass: classes.family,
          optionsKey: 'jobFamilies',
          shouldDisableSort: true,
          isClearable: true,
          parser: {
            value: 'id',
            label: 'name',
          },
        },
        {
          name: 'job_type',
          type: SELECT,
          fieldWrapperClass: classes.type,
          optionsKey: 'jobTitleTypes',
          isSearchDisabled: true,
          shouldDisableSort: true,
          required: true,
          parser: {
            value: 'id',
            label: 'name',
          },
          validators: [
            {
              validator: isEmpty,
              type: 'required',
            },
          ],
        },
        {
          name: 'is_key',
          type: CHECKBOX,
          fieldWrapperClass: classes.keyJob,
          className: classes.keyJobCheckbox,
          isControlled: true,
          dependants: [
            {
              name: 'criticality',
              resetFnc: (_previous, nextValue, currentValue) =>
                nextValue ? currentValue ?? null : null,
            },
          ],
        },
        {
          name: 'criticality',
          type: SELECT,
          fieldWrapperClass: classes.criticality,
          optionsKey: 'criticalityLevels',
          shouldDisableSort: true,
          isClearable: true,
          required: true,
          isDisabledCheck: values => !values?.is_key,
          parser: {
            value: 'id',
            label: 'label',
          },
          validators: [
            {
              type: 'required',
              shouldPassFormValues: true,
              validator: (value, values) => !values?.is_key || isEmpty(value),
            },
          ],
        },
      ]
    : []),
  {
    name: 'tags',
    isDisabled: !canManageJobTitle,
    type: SELECT,
    fieldWrapperClass: classes.tags,
    optionsKey: 'categories',
    creatableOptionField: CREATABLE_TAG_FIELD,
    isCategorized: true,
    hasCategoryColorBox: true,
    isMulti: true,
    hasHash: true,
    isCreatable: true,
    isTag: true,
    parser: {
      value: 'id',
      label: 'name',
      categoryLabel: 'name',
      categoryValue: 'tags',
    },
    onCreateOption: onCreateTag,
  },
  {
    name: 'description',
    type: FIELD_TYPES.RICH_TEXT_EDITOR,
    fieldWrapperClass: classes.descriptionSection,
    isDisabled: !canManageJobTitle,
    isReadOnly: !canManageJobTitle,
    shouldRemoveFieldSpacing: true,
    validators: [
      {
        validator: fieldValue => validateLength(fieldValue, 0, 10000),
        type: 'descriptionMaxLength',
      },
    ],
  },
];

export const preparePageFilters = (
  translations,
  categories,
  quickFilters = []
) => {
  const { TAGS } = PARAMS;

  return [
    {
      id: TAGS,
      name: translations.filters.labels[TAGS],
      icon: TagsIcon,
      categoryItemsKey: TAGS,
      hasCategoryColorBox: true,
      hasSubmenuHash: true,
      isCategorized: true,
      items: categories,
      categorySearch: filterTags,
    },
    ...(!isArrayEmpty(quickFilters)
      ? quickFilters?.map(filter => {
          return {
            id: filter.name,
            name: filter.name,
            icon: () => {
              return <ColorBox bgColor={filter.color} isSmall />;
            },
            parent: {
              id: TAGS,
            },
            items: filter?.tags,
          };
        }) || {}
      : []),
  ];
};
